<template>
  <div class="sf-page sf-floor-plan-page">
    <top-header v-bind:subtitle="$t('subtitle')"
                v-bind:title="$t('floorPlan')" />
    <div class="sf-container">
      <v-card class="sf-card sf-floor-plan"
              flat>
        <div class="sf-toolbar__row sf-floor-plan__toolbar">
          <v-autocomplete class="sf-search sf-toolbar__search"
                          clearable
                          dense
                          flat
                          hide-details
                          item-text="text"
                          item-value="number"
                          prepend-inner-icon="ti-search"
                          solo
                          v-bind:items="searchItems"
                          v-bind:loading="loading"
                          v-bind:menu-props="{ maxHeight: 320 }"
                          v-bind:no-data-text="$t('noResults')"
                          v-bind:placeholder="$t('searchPlaceholder')"
                          v-model="selectedNumber"
                          v-on:change="onSearchSelect">
            <template v-slot:item="{ item }">
              <v-list-item-avatar class="sf-avatar"
                                  size="32">
                <span>{{ item.number }}</span>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ item.name || $t('freeStand') }}</v-list-item-title>
                <v-list-item-subtitle>{{ $t('stand') }} {{ item.number }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
          <v-btn class="sf-floor-plan__open"
                 color="primary"
                 depressed
                 v-if="selectedAssignment != null"
                 v-on:click="openSupplier({ assignment: selectedAssignment })">
            {{ $t('openOrderSheet') }}
            <v-icon right
                    small>ti-arrow-right</v-icon>
          </v-btn>
          <span class="sf-toolbar__hint hidden-sm-and-down"
                v-if="!loading">
            {{ $tc('assignedCount', assignedCount, { count: assignedCount }) }}
          </span>
        </div>

        <div class="sf-floor-plan__canvas">
          <floor-plan mode="customer"
                      ref="plan"
                      v-bind:assignments="assignments"
                      v-bind:plan="plan"
                      v-on:select="openSupplier" />
        </div>

        <div class="sf-floor-plan__footer">
          <ul class="sf-floor-plan__legend">
            <li><span class="sf-floor-plan__swatch sf-floor-plan__swatch--assigned"></span>{{ $t('legendAssigned') }}</li>
            <li><span class="sf-floor-plan__swatch sf-floor-plan__swatch--empty"></span>{{ $t('legendEmpty') }}</li>
            <li><span class="sf-floor-plan__swatch sf-floor-plan__swatch--sagasser"></span>{{ $t('legendSagasser') }}</li>
            <li><span class="sf-floor-plan__swatch sf-floor-plan__swatch--service"></span>{{ $t('legendService') }}</li>
          </ul>
          <p class="sf-floor-plan__hint">{{ $t('hint') }}</p>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'

  import settings from '../settings'
  import plan from '@/assets/floorplan.json'
  import { buildAssignments, planNumbers } from '@/lib/floorplan/booths'

  import FloorPlan from '@/components/floorplan/FloorPlan'
  import TopHeader from '@/components/common/TopHeader'

  export default {
    components: {
      FloorPlan,
      TopHeader
    },
    computed: {
      assignedCount() {
        return Object.keys(this.assignments).length
      },
      assignments() {
        return buildAssignments(this.suppliers, { apiUri: settings.apiUri })
      },
      searchItems() {
        return planNumbers(this.plan).map(item => {
          const assignment = this.assignments[item.number]

          return {
            number: item.number,
            name: assignment != null ? assignment.name : item.label,
            text: assignment != null ? `${assignment.name} · ${this.$t('stand')} ${item.number}` : `${this.$t('stand')} ${item.number}${item.label ? ` · ${item.label}` : ''}`
          }
        })
      },
      selectedAssignment() {
        return this.selectedNumber != null ? this.assignments[this.selectedNumber] || null : null
      },
      ...mapGetters([
        'suppliers'
      ])
    },
    async created() {
      this.loading = true
      await this.$store.dispatch('loadSuppliersForMe')
      this.loading = false

      if (this.$route.query.stand != null) {
        this.selectedNumber = String(this.$route.query.stand)
        this.$nextTick(() => this.focus(this.selectedNumber))
      }
    },
    data: () => ({
      loading: false,
      plan,
      selectedNumber: null
    }),
    methods: {
      focus(number) {
        if (this.$refs.plan != null && number != null) {
          this.$refs.plan.focusStand(String(number))
        }
      },
      onSearchSelect(number) {
        this.focus(number)
      },
      openSupplier({ assignment }) {
        if (assignment == null) {
          return
        }

        this.$router.push({ name: 'supplier', params: { id: assignment.supplierId }, query: { from: 'floor-plan' } })
      }
    },
    name: 'FloorPlanView'
  }
</script>

<i18n>
  {
    "de": {
      "assignedCount": "Kein Stand belegt | {count} Stand belegt | {count} Stände belegt",
      "floorPlan": "Lageplan",
      "freeStand": "Freier Stand",
      "hint": "Plan mit der Maus oder dem Finger verschieben, mit Mausrad, Tasten oder zwei Fingern zoomen. Ab einer gewissen Vergrößerung erscheinen die Logos. Ein Tipp auf einen Stand öffnet den Ordersatz.",
      "legendAssigned": "Lieferant",
      "legendEmpty": "Freier Stand",
      "legendSagasser": "SAGASSER",
      "legendService": "Service / Verpflegung",
      "noResults": "Kein Stand gefunden",
      "openOrderSheet": "Ordersatz öffnen",
      "searchPlaceholder": "Lieferant oder Standnummer suchen",
      "stand": "Stand",
      "subtitle": "Finden Sie Ihre Lieferanten in der Messehalle."
    },
    "en": {
      "assignedCount": "No booth assigned | {count} booth assigned | {count} booths assigned",
      "floorPlan": "Floor plan",
      "freeStand": "Free booth",
      "hint": "Drag the plan with the mouse or your finger, zoom with the mouse wheel, the buttons or two fingers. Logos appear when zoomed in. Tap a booth to open the order sheet.",
      "legendAssigned": "Supplier",
      "legendEmpty": "Free booth",
      "legendSagasser": "SAGASSER",
      "legendService": "Service / catering",
      "noResults": "No booth found",
      "openOrderSheet": "Open order sheet",
      "searchPlaceholder": "Search supplier or booth number",
      "stand": "Booth",
      "subtitle": "Find your suppliers in the exhibition hall."
    }
  }
</i18n>

<style lang="scss" scoped>
  .sf-floor-plan {
    overflow: hidden;
  }

  .sf-floor-plan__toolbar {
    flex-wrap: wrap;
  }

  .sf-floor-plan__canvas {
    border-top: 1px solid var(--sf-border);
    height: clamp(420px, calc(100vh - 330px), 780px);
  }

  .sf-floor-plan__footer {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: var(--sf-space-2) var(--sf-space-6);
    justify-content: space-between;
    padding: var(--sf-space-3) var(--sf-space-4);
  }

  .sf-floor-plan__legend {
    display: flex;
    flex-wrap: wrap;
    gap: var(--sf-space-2) var(--sf-space-4);
    list-style: none;
    margin: 0;
    padding: 0;

    li {
      align-items: center;
      color: var(--sf-text-secondary);
      display: flex;
      font-size: .8125rem;
      gap: 6px;
    }
  }

  .sf-floor-plan__swatch {
    border: 1px solid var(--sf-border-strong);
    border-radius: 3px;
    display: inline-block;
    height: 12px;
    width: 12px;

    &--assigned { background: #ffffff; }
    &--empty { background: #f3f5f9; border-style: dashed; }
    &--sagasser { background: #e4ecf7; border-color: #aec3e0; }
    &--service { background: #e9ecf1; }
  }

  .sf-floor-plan__hint {
    color: var(--sf-text-muted);
    font-size: .8125rem;
    margin: 0;
  }

  @media (max-width: 599px) {
    .sf-floor-plan__canvas {
      height: calc(100vh - 250px);
      min-height: 420px;
    }

    .sf-floor-plan__open {
      width: 100%;
    }
  }
</style>
